import { createBrowserRouter } from 'react-router';
import { useTranslation } from 'react-i18next';
import { RootBoundary } from './components/shared/RootBoundary';
import { RequireAuth, RedirectIfAuthed } from './components/shared/RequireAuth';
import { RequireWorkspace } from './components/shared/RequireWorkspace';
import { RequireRole } from './components/shared/RequireRole';
import { AppShell } from './components/layout/AppShell';
import { ComingSoon } from './components/shared/ComingSoon';
import { LoginPage } from './features/auth/LoginPage';
import { SignupPage } from './features/auth/SignupPage';
import { ForgotPasswordPage } from './features/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './features/auth/ResetPasswordPage';
import { AcceptInvitePage } from './features/auth/AcceptInvitePage';
import { OnboardingWizard } from './features/onboarding/OnboardingWizard';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { WorkspacePage } from './features/workspace/WorkspacePage';
import { CustomersPage } from './features/customers/CustomersPage';
import { CustomerDetailPage } from './features/customers/CustomerDetailPage';
import { LeadsPage } from './features/leads/LeadsPage';
import { QuotesPage } from './features/quotes/QuotesPage';
import { NewQuotePage } from './features/quotes/NewQuotePage';
import { QuoteDetailPage } from './features/quotes/QuoteDetailPage';
import { InventoryPage } from './features/inventory/InventoryPage';
import { PrintWrapsPage } from './features/print-wraps/PrintWrapsPage';
import { NewPrintDesignPage } from './features/print-wraps/NewPrintDesignPage';
import { PrintDesignEditorPage } from './features/print-wraps/PrintDesignEditorPage';
import { PublicQuotePage } from './features/public-quote/PublicQuotePage';
import { JobsPage } from './features/jobs/JobsPage';
import { JobDetailPage } from './features/jobs/JobDetailPage';
import { CalendarPage } from './features/calendar/CalendarPage';
import { SettingsPage } from './features/settings/SettingsPage';
import { PricingPage } from './features/settings/PricingPage';
import { ReportsPage } from './features/reports/ReportsPage';
import { IntegrationsPage } from './features/integrations/IntegrationsPage';
import { QuoteWidgetDetailPage } from './features/integrations/QuoteWidgetDetailPage';
import { DirectoryListingDetailPage } from './features/integrations/DirectoryListingDetailPage';

function NotFound() {
  const { t } = useTranslation();
  return <div className="grid min-h-screen place-items-center text-muted">{t('notFound')}</div>;
}

// Route map per §15.2. `/` serves the authenticated Dashboard directly -
// the Astro marketing site is hosted separately and owns the marketing
// landing page. Screens not yet built land behind <ComingSoon>, mirroring
// the original prototype's own placeholder-screen pattern.
export const router = createBrowserRouter([
  {
    path: '/',
    errorElement: <RootBoundary />,
    children: [
      {
        element: <RedirectIfAuthed />,
        children: [
          { path: 'login', element: <LoginPage /> },
          { path: 'signup', element: <SignupPage /> },
          { path: 'forgot-password', element: <ForgotPasswordPage /> },
        ],
      },
      // reset-password and accept-invite are NOT wrapped in RedirectIfAuthed:
      // Supabase's password-recovery link signs the user in before they set a
      // new password, and the invite flow requires signing in as a step, so
      // redirecting an authed session away from either would break the flow.
      { path: 'reset-password', element: <ResetPasswordPage /> },
      { path: 'accept-invite', element: <AcceptInvitePage /> },
      { path: 'q/:token', element: <PublicQuotePage /> },
      {
        element: <RequireAuth />,
        children: [
          { path: 'onboarding', element: <OnboardingWizard /> },
          {
            element: <RequireWorkspace />,
            children: [
              {
                element: <AppShell />,
                children: [
                  { index: true, element: <DashboardPage /> },
                  {
                    element: <RequireRole hiddenFor={['installer']} />,
                    children: [
                      { path: 'leads', element: <LeadsPage /> },
                      { path: 'quotes', element: <QuotesPage /> },
                      { path: 'quotes/new', element: <NewQuotePage /> },
                      { path: 'quotes/:quoteId', element: <QuoteDetailPage /> },
                      { path: 'customers', element: <CustomersPage /> },
                      { path: 'customers/:customerId', element: <CustomerDetailPage /> },
                      { path: 'inventory', element: <InventoryPage /> },
                      { path: 'print-wraps', element: <PrintWrapsPage /> },
                      { path: 'print-wraps/new', element: <NewPrintDesignPage /> },
                      { path: 'print-wraps/:designId', element: <PrintDesignEditorPage /> },
                    ],
                  },
                  { path: 'jobs', element: <JobsPage /> },
                  { path: 'jobs/:jobId', element: <JobDetailPage /> },
                  { path: 'calendar', element: <CalendarPage /> },
                  {
                    element: <RequireRole hiddenFor={['installer', 'front_desk']} />,
                    children: [
                      { path: 'pricing', element: <PricingPage /> },
                      { path: 'reports', element: <ReportsPage /> },
                    ],
                  },
                  {
                    element: <RequireRole hiddenFor={['manager', 'front_desk', 'installer']} />,
                    children: [
                      { path: 'integrations', element: <IntegrationsPage /> },
                      { path: 'integrations/quote-widget', element: <QuoteWidgetDetailPage /> },
                      { path: 'integrations/directory-listing', element: <DirectoryListingDetailPage /> },
                    ],
                  },
                  { path: 'settings', element: <SettingsPage /> },
                  { path: 'workspace', element: <WorkspacePage /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <NotFound /> },
]);
